import { ACTION_LABEL } from "@/lib/i18nLabels";
import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatDetail } from "@/lib/api";
import ParentProjectPicker from "./project-dialog/ParentProjectPicker";

/**
 * BulkChangeParentDialog — 149-ak.
 *
 * Dipakai di Monitoring WO utk pindahkan banyak WO sekaligus ke parent baru.
 * Props:
 *  - open, onOpenChange (dialog control)
 *  - childProjects: array of WO/VO doc terpilih
 *  - onSaved: callback setelah sukses (untuk reload parent list)
 */
export default function BulkChangeParentDialog({ open, onOpenChange, childProjects, onSaved }) {
  const [newParentId, setNewParentId] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!newParentId) {
      toast.error("Pilih parent project baru terlebih dahulu.");
      return;
    }
    setBusy(true);
    try {
      const child_ids = childProjects.map((c) => c.id);
      const { data } = await api.post("/active-projects/bulk-change-parent", {
        child_ids,
        new_parent_id: newParentId,
      });
      const movedN = data.moved?.length || 0;
      const skippedN = data.skipped?.length || 0;
      if (movedN > 0) {
        toast.success(`${movedN} WO/VO dipindah ke "${data.new_parent?.name}"${skippedN > 0 ? ` (${skippedN} dilewati)` : ""}`);
      } else {
        toast.warning(`Tidak ada WO/VO yang dipindah. ${skippedN} dilewati.`);
      }
      if (skippedN > 0) {
        console.warn("Bulk change parent — skipped:", data.skipped);
      }
      onOpenChange(false);
      setNewParentId(null);
      onSaved?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // Fake "project" object utk ParentProjectPicker — supaya bisa detect "changed" state
  const fakeProject = { parent_project_id: null };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) setNewParentId(null); } }}>
      <DialogContent className="rounded-none max-w-lg" data-testid="dialog-bulk-change-parent">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Pindah Parent · {childProjects.length} WO/VO</DialogTitle>
          <DialogDescription>
            Semua WO/VO terpilih akan pindah ke parent yang sama. Mirror field (nama parent, client, lokasi) auto-sync. Perubahan direkam di edit history tiap child.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="border border-slate-200 bg-slate-50 px-3 py-2 text-xs max-h-32 overflow-y-auto" data-testid="bulk-target-list">
            <div className="overline text-slate-500 mb-1">Target ({childProjects.length})</div>
            {childProjects.map((c) => (
              <div key={c.id} className="flex items-center gap-2 py-0.5">
                <span className={`text-[9px] font-bold px-1 py-0.5 uppercase tracking-wider border ${c.project_type === "working_order" ? "bg-sky-50 text-sky-700 border-sky-300" : "bg-orange-50 text-orange-700 border-orange-300"}`}>
                  {c.project_type === "working_order" ? "WO" : "VO"}
                </span>
                <span className="font-mono text-[10px] font-semibold text-[#0B1D3A]">{c.wo_number || c.variation_order_name || c.id.slice(0, 8)}</span>
                <span className="text-[10px] text-slate-500 truncate">{c.parent_project_name || c.project_name || "-"}</span>
              </div>
            ))}
          </div>

          <ParentProjectPicker
            project={fakeProject}
            value={newParentId}
            onChange={setNewParentId}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" className="rounded-none" disabled={busy} onClick={() => onOpenChange(false)}>{ACTION_LABEL.cancel}</Button>
          <Button type="button" data-testid="bulk-change-parent-submit" disabled={busy || !newParentId} onClick={submit} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]">
            {busy ? "Memindahkan…" : `Pindahkan ${childProjects.length} WO/VO`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
